import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CAlert, CButton, CCard, CCardBody, CCol, CRow } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilBolt,
  cilHistory,
  cilPeople,
  cilReload,
  cilSpeedometer,
  cilWarning,
} from '@coreui/icons'
import CommonService from 'src/service/CommonService'
import TripleCap from './TripleCap'
import PowerUsedPlayer from './PowerUsedPlayer'
import './PowerControl.css'

const PowerControl = () => {
  const [data, setData] = useState(null)
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [settingsError, setSettingsError] = useState(false)
  const [tripleCapLoader, setTripleCapLoader] = useState(false)
  const [tripleCheck, setTripleCheck] = useState(false)
  const [powerUsers, setPowerUsers] = useState([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historyError, setHistoryError] = useState(false)

  const loadSettings = useCallback(() => {
    setSettingsLoading(true)
    setSettingsError(false)
    CommonService.powerControl()
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setTripleCheck(!!res.data?.captain_cards_status)
        } else {
          setSettingsError(true)
        }
      })
      .catch(() => setSettingsError(true))
      .finally(() => setSettingsLoading(false))
  }, [])

  const loadHistory = useCallback(() => {
    setHistoryLoading(true)
    setHistoryError(false)
    CommonService.usedPowerHistory()
      .then((res) => {
        if (res.status === 200) {
          setPowerUsers(res.data || [])
        } else {
          setHistoryError(true)
        }
      })
      .catch(() => setHistoryError(true))
      .finally(() => setHistoryLoading(false))
  }, [])

  useEffect(() => {
    loadSettings()
    loadHistory()
  }, [loadSettings, loadHistory])

  // One member can play the power more than once, so the member count is the distinct users.
  const membersUsing = useMemo(
    () => new Set(powerUsers.map((item) => item.user_id ?? item.username).filter(Boolean)).size,
    [powerUsers],
  )

  const statCards = useMemo(
    () => [
      {
        key: 'status',
        label: 'Triple Captain',
        value: settingsLoading ? '—' : tripleCheck ? 'Active' : 'Inactive',
        icon: cilBolt,
        tone: settingsLoading ? 'secondary' : tripleCheck ? 'success' : 'secondary',
      },
      {
        key: 'limit',
        label: 'Use Limit',
        value: settingsLoading ? '—' : data?.captain_cards ?? '—',
        icon: cilSpeedometer,
        tone: 'primary',
      },
      {
        key: 'used',
        label: 'Times Used',
        value: historyLoading ? '—' : powerUsers.length,
        icon: cilHistory,
        tone: 'info',
      },
      {
        key: 'members',
        label: 'Members Using',
        value: historyLoading ? '—' : membersUsing,
        icon: cilPeople,
        tone: 'warning',
      },
    ],
    [settingsLoading, tripleCheck, data, historyLoading, powerUsers, membersUsing],
  )

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Powers</h1>
        <p className="ge-page-subtitle">
          Turn the Triple Captain power on or off, set how often it can be played, and see who has
          used it.
        </p>
      </div>

      {settingsError && (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load the power settings. Nothing has been changed.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={loadSettings}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      )}

      <CRow className="g-3 mb-3">
        {statCards.map((card) => (
          <CCol key={card.key} sm={6} lg={3}>
            <CCard className="pc-stat-card h-100">
              <CCardBody className="d-flex align-items-center gap-3">
                <span className={`pc-stat-icon pc-stat-icon--${card.tone}`}>
                  <CIcon icon={card.icon} size="lg" />
                </span>
                <div>
                  <div className="pc-stat-label">{card.label}</div>
                  <div className="pc-stat-value">{card.value}</div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <CRow className="g-3">
        <CCol xs={12}>
          <TripleCap
            data={data}
            loading={settingsLoading}
            tripleCheck={tripleCheck}
            setTripleCheck={setTripleCheck}
            tripleCapLoader={tripleCapLoader}
            setTripleCapLoader={setTripleCapLoader}
            setData={setData}
          />
        </CCol>
        <CCol xs={12}>
          <PowerUsedPlayer
            powerUsers={powerUsers}
            loading={historyLoading}
            error={historyError}
            onRetry={loadHistory}
          />
        </CCol>
      </CRow>
    </>
  )
}

export default PowerControl
